import * as React from 'react';
import { ChevronDown, Download, Upload } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Sheet,
  SheetStat,
  WorkspaceButton,
  WorkspaceMenu,
  WorkspaceNotice,
  linkishClass,
} from '@/components/ui/workspace';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { StageProgressBar } from '@/components/ui/stage-progress';
import { StatusChip } from '@/components/ui/status-chip';
import { UploadProgressBar } from '@/components/ui/upload-progress';
import { IMPORT_ACCEPT, fileProblem } from '@/components/requirement-import/import-logic';
import type { ModuleOption } from '@/components/requirement-import/module-select';
import { ApiError } from '@/lib/api/client';
import { newIdempotencyKey } from '@/lib/api/engagement-requirements';
import {
  downloadMatchReview,
  requirementMatchKeys,
  uploadMatchRun,
  useApplyMatchRun,
  useDecideMatchRow,
  useDecideMatchRows,
  useMatchRestorePreview,
  useMatchRunQuery,
  useMatchRunsQuery,
  useRestoreMatchRun,
  useRunMatchAgain,
  type ApplyResult,
  type MatchRunSummary,
  type RestorePreview,
  type RestoreResult,
} from '@/lib/api/requirement-matches';
import type { UploadProgress } from '@/lib/api/upload';
import { saveBlob } from '@/lib/utils/download';
import { cn } from '@/lib/utils/cn';
import { formatDate } from '../requirements-logic';
import { MatchReview } from './match-review';
import {
  applyLabel,
  applyResultMessage,
  isFullyApplied,
  plural,
  progressText,
  readyCounts,
  restoreLines,
  summaryText,
} from './match-logic';

/**
 * "Match a spreadsheet to the library" (T-0264; E14-design §2; ADR-025
 * decision 1). One sheet with visible stages, like the import: drop the
 * file, watch it being matched, then review three groups and apply.
 *
 *   - The file goes up once (the import's picker and limits). The run is
 *     kept on the server: closing the sheet does not stop it, and the
 *     sheet lists the latest runs to open again.
 *   - While the judge works the run is read every two seconds and the
 *     progress shows the rows matched so far, a count kept by code.
 *   - Same rows are preselected. Nothing is added until Apply, which sends
 *     an Idempotency-Key (reused when the same apply is retried) and adds
 *     only the rows decided and not yet added, so it can be pressed again
 *     after more decisions.
 *   - Export review list downloads the Broader and No match rows, or all
 *     rows; each row carries its candidates' meaning scores (what the
 *     meaning floors are calibrated from).
 *   - A run applied before Caliber kept the client's wording (24 October
 *     2026) offers "Restore the spreadsheet's wording and areas": the
 *     server's preview says what would change, and Restore applies it with
 *     an Idempotency-Key. The card shows only while there is something to
 *     restore.
 */

export interface LibraryMatchSheetProps {
  engagementId: string;
  /** "Client · Engagement", for the sheet description. */
  engagementLabel: string | null;
  /** Active library modules, for Add as written. */
  modules: ReadonlyArray<ModuleOption>;
  onClose: () => void;
  /** Show the requirements one apply added (the list filtered to its load). */
  onShowLoad: (loadId: string) => void;
}

const STATUS_LABEL: Record<MatchRunSummary['status'], string> = {
  matching: 'Matching',
  ready: 'To review',
  applied: 'Applied',
  failed: 'Stopped',
};

const STATUS_TONE: Record<
  MatchRunSummary['status'],
  'neutral' | 'at-risk' | 'healthy' | 'critical'
> = {
  matching: 'neutral',
  ready: 'at-risk',
  applied: 'healthy',
  failed: 'critical',
};

function messageOf(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

export function LibraryMatchSheet({
  engagementId,
  engagementLabel,
  modules,
  onClose,
  onShowLoad,
}: LibraryMatchSheetProps) {
  const qc = useQueryClient();
  const [runId, setRunId] = React.useState<string | null>(null);
  const [upload, setUpload] = React.useState<{
    name: string;
    progress: UploadProgress | null;
    processing: boolean;
    controller: AbortController;
  } | null>(null);
  const [uploadError, setUploadError] = React.useState<string | null>(null);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [lastApply, setLastApply] = React.useState<ApplyResult | null>(null);
  const [dragOver, setDragOver] = React.useState(false);
  const [exporting, setExporting] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const applyKey = React.useRef<{ signature: string; key: string } | null>(null);

  const runs = useMatchRunsQuery(engagementId, runId === null);
  const runQuery = useMatchRunQuery(engagementId, runId);
  const run = runQuery.data ?? null;
  const decideOne = useDecideMatchRow(engagementId, runId ?? '');
  const decideMany = useDecideMatchRows(engagementId, runId ?? '');
  const applyMutation = useApplyMatchRun(engagementId, runId ?? '');
  const again = useRunMatchAgain(engagementId, runId ?? '');
  const restorable = (run?.counts.added ?? 0) > 0 && run?.status !== 'matching';
  const restorePreview = useMatchRestorePreview(engagementId, runId ?? '', restorable);
  const restoreMutation = useRestoreMatchRun(engagementId, runId ?? '');
  const restoreKey = React.useRef<{ signature: string; key: string } | null>(null);
  const [restored, setRestored] = React.useState<RestoreResult | null>(null);
  const busy =
    decideOne.isPending ||
    decideMany.isPending ||
    applyMutation.isPending ||
    again.isPending ||
    restoreMutation.isPending;

  // An upload still under way when the sheet closes is cancelled.
  const uploading = React.useRef<AbortController | null>(null);
  React.useEffect(() => () => uploading.current?.abort(), []);

  const open = (id: string) => {
    setRunId(id);
    setActionError(null);
    setLastApply(null);
    setRestored(null);
  };

  const startUpload = (file: File | undefined) => {
    if (!file) return;
    setUploadError(null);
    const problem = fileProblem(file);
    if (problem) {
      setUploadError(problem);
      return;
    }
    const controller = new AbortController();
    uploading.current = controller;
    setUpload({ name: file.name, progress: null, processing: false, controller });
    uploadMatchRun(engagementId, file, controller.signal, {
      onProgress: (progress) => setUpload((u) => (u ? { ...u, progress } : u)),
      onUploaded: () => setUpload((u) => (u ? { ...u, processing: true } : u)),
    }).then(
      (created) => {
        uploading.current = null;
        qc.setQueryData(requirementMatchKeys.run(engagementId, created.id), created);
        setUpload(null);
        open(created.id);
      },
      (err: unknown) => {
        uploading.current = null;
        setUpload(null);
        if (err instanceof ApiError && err.code === 'aborted') return;
        setUploadError(messageOf(err, 'The file could not be read.'));
      },
    );
  };

  const fail = (fallback: string) => (err: unknown) => setActionError(messageOf(err, fallback));

  const apply = () => {
    if (!run || busy) return;
    // One key per distinct apply (the rows ready and what each adds); a
    // retry after a lost answer reuses it.
    const signature = [run.groups.same, run.groups.broader, run.groups.none]
      .flat()
      .filter((r) => r.ready)
      .map(
        (r) =>
          `${r.id}:${r.decision ?? ''}:${r.decision_library_item_id ?? ''}:${r.decision_module?.id ?? ''}`,
      )
      .join('|');
    if (applyKey.current?.signature !== signature) {
      applyKey.current = { signature, key: newIdempotencyKey() };
    }
    setActionError(null);
    applyMutation.mutateAsync(applyKey.current.key).then(
      (result) => {
        applyKey.current = null;
        // The run is read again with the requirements (useApplyMatchRun).
        setLastApply(result);
      },
      (err: unknown) => {
        if (!(err instanceof ApiError) || err.code !== 'network') applyKey.current = null;
        fail('The rows could not be added. Nothing was saved.')(err);
      },
    );
  };

  const restore = (preview: RestorePreview) => {
    if (busy) return;
    // One key per preview; a retry after a lost answer reuses it.
    const signature = JSON.stringify(preview);
    if (restoreKey.current?.signature !== signature) {
      restoreKey.current = { signature, key: newIdempotencyKey() };
    }
    setActionError(null);
    restoreMutation.mutateAsync(restoreKey.current.key).then(
      (result) => {
        restoreKey.current = null;
        setRestored(result);
        void restorePreview.refetch();
      },
      (err: unknown) => {
        if (!(err instanceof ApiError) || err.code !== 'network') restoreKey.current = null;
        fail('The wording and areas were not restored. Nothing was changed.')(err);
      },
    );
  };

  const exportReview = (rows: 'review' | 'all') => {
    if (!run || exporting) return;
    setExporting(true);
    downloadMatchReview(engagementId, run.id, rows)
      .then((file) => saveBlob(file.fileName, file.blob), fail('The review list was not produced.'))
      .finally(() => setExporting(false));
  };

  const counts = run ? readyCounts(run) : null;
  const reviewable = run && run.status !== 'matching';
  const done = run ? isFullyApplied(run) : false;

  return (
    <Sheet
      open
      onOpenChange={(next) => {
        if (!next && !busy) onClose();
      }}
      closeOnScrimClick={false}
      onEscapeKeyDown={(e) => {
        // Esc closes an open module list first, not the whole sheet.
        if (document.querySelector('[data-import-popup-open]')) e.preventDefault();
      }}
      title="Match a spreadsheet to the library"
      description={`${engagementLabel ? `Into ${engagementLabel}. ` : ''}Nothing is added until you apply.`}
      headerActions={
        reviewable ? (
          <WorkspaceMenu
            aria-label="Export review list"
            label="Export to .xlsx"
            align="end"
            items={[
              {
                id: 'review',
                label: 'Broader and No match',
                hint: ((run?.counts.broader ?? 0) + (run?.counts.none ?? 0)).toLocaleString(),
                onSelect: () => exportReview('review'),
              },
              {
                id: 'all',
                label: 'All rows',
                hint: (run?.counts.rows ?? 0).toLocaleString(),
                onSelect: () => exportReview('all'),
              },
            ]}
            trigger={
              <WorkspaceButton size="md" disabled={exporting}>
                <Download className="h-3.5 w-3.5" aria-hidden="true" />
                {exporting ? 'Exporting…' : 'Export review list'}
                <ChevronDown className="h-3 w-3" aria-hidden="true" />
              </WorkspaceButton>
            }
          />
        ) : undefined
      }
      summary={
        run && reviewable ? (
          <>
            <SheetStat value={run.counts.same.toLocaleString()} label="same" />
            <SheetStat value={run.counts.broader.toLocaleString()} label="broader" />
            <SheetStat value={run.counts.none.toLocaleString()} label="no match" />
            <SheetStat value={(counts?.ready ?? 0).toLocaleString()} label="ready to add" />
          </>
        ) : undefined
      }
      actions={
        <>
          <WorkspaceButton size="md" disabled={busy} onClick={onClose}>
            Close
          </WorkspaceButton>
          {reviewable && !done ? (
            <WorkspaceButton
              size="md"
              variant="primary"
              disabled={busy || (counts?.ready ?? 0) === 0}
              onClick={apply}
            >
              {applyMutation.isPending ? 'Adding…' : applyLabel(run)}
            </WorkspaceButton>
          ) : null}
        </>
      }
    >
      <div
        data-testid="library-match-body"
        className="flex min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-y-auto bg-paper-50 p-5 [&>*]:shrink-0"
        onDragOver={(e) => {
          if (runId !== null) return;
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          if (runId !== null) return;
          e.preventDefault();
          setDragOver(false);
          startUpload(e.dataTransfer?.files?.[0]);
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept={IMPORT_ACCEPT}
          aria-label="Choose a spreadsheet"
          className="sr-only"
          onChange={(e) => {
            startUpload(e.target.files?.[0]);
            e.target.value = '';
          }}
        />

        {runId === null ? (
          upload ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 rounded-large border border-ink-100 bg-paper-0 p-10 text-center text-body-sm text-ink-500">
              <div role="status" className="flex flex-col items-center gap-2">
                <Spinner />
                <span className="font-medium text-ink-900">Reading {upload.name}…</span>
                Finding the sheet, the header row and the columns.
              </div>
              <UploadProgressBar
                className="max-w-[360px]"
                fileName={upload.name}
                progress={upload.progress}
                processing={upload.processing}
              />
              <WorkspaceButton className="mt-1" onClick={() => upload.controller.abort()}>
                Cancel upload
              </WorkspaceButton>
            </div>
          ) : (
            <>
              {uploadError ? (
                <WorkspaceNotice tone="critical">
                  <div className="font-semibold text-ink-900">The file was not matched</div>
                  <p className="mt-0.5">{uploadError}</p>
                </WorkspaceNotice>
              ) : null}
              <div
                className={cn(
                  'flex flex-col items-center justify-center gap-3 rounded-large border-[1.5px] border-dashed p-10 text-center',
                  dragOver
                    ? 'border-accent-amber-600 bg-accent-amber-50'
                    : 'border-ink-200 bg-paper-0',
                )}
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-card bg-paper-100 text-ink-700">
                  <Upload className="h-[18px] w-[18px]" aria-hidden="true" />
                </span>
                <div className="text-h3 font-semibold text-ink-900">
                  Drop the client&rsquo;s spreadsheet here
                </div>
                <p className="max-w-[480px] text-meta font-medium text-ink-500">
                  .xlsx, .xls, .xlsm or .csv, up to 20 MB and 10,000 rows. Each row is matched to
                  the firm library item closest in meaning. Rows that match the same thing are ready
                  to add; broader matches and rows with no match wait for you.
                </p>
                <WorkspaceButton
                  size="md"
                  variant="primary"
                  onClick={() => inputRef.current?.click()}
                >
                  Choose a file
                </WorkspaceButton>
                <p className="text-meta text-ink-500">
                  The file is not kept. Each added requirement cites its file, sheet and row.
                </p>
              </div>
              <EarlierRuns
                runs={runs.data?.items ?? []}
                loading={runs.isLoading}
                error={runs.error}
                onOpen={open}
              />
            </>
          )
        ) : runQuery.error ? (
          <ErrorMessage error={runQuery.error} />
        ) : !run ? (
          <div className="flex items-center gap-2 text-body-sm text-ink-500">
            <Spinner /> Loading the match…
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" className={linkishClass()} onClick={() => setRunId(null)}>
                All matches
              </button>
              <span className="min-w-0 truncate font-mono text-meta font-medium text-ink-900">
                {run.file_name}
              </span>
              <StatusChip tone={STATUS_TONE[run.status]}>{STATUS_LABEL[run.status]}</StatusChip>
              <span className="text-meta text-ink-500">{summaryText(run)}</span>
            </div>

            {run.status === 'matching' ? (
              <div
                role="status"
                className="flex flex-col items-center gap-3 rounded-large border border-ink-100 bg-paper-0 p-10 text-center text-body-sm text-ink-500"
              >
                <Spinner />
                <span className="font-medium text-ink-900">{progressText(run)}</span>
                <StageProgressBar
                  className="max-w-[360px]"
                  percent={
                    run.counts.rows > 0
                      ? Math.floor((run.counts.matched / run.counts.rows) * 100)
                      : null
                  }
                  label={`${run.counts.matched.toLocaleString()} of ${plural(run.counts.rows, 'row')} matched`}
                  ariaLabel={`Matching ${run.file_name}`}
                  tone="ai"
                />
                <p className="max-w-[460px] text-meta">
                  The AI compares each row with the five library items closest in meaning, twenty
                  rows at a time. You can close this; the match keeps going and is listed here when
                  you come back.
                </p>
              </div>
            ) : null}

            {run.status === 'failed' ? (
              <WorkspaceNotice tone="critical">
                <div className="font-semibold text-ink-900">Matching stopped</div>
                <p className="mt-0.5">{run.reason}</p>
                <div className="mt-2">
                  <WorkspaceButton
                    disabled={busy}
                    onClick={() =>
                      again
                        .mutateAsync()
                        .then(
                          () => setActionError(null),
                          fail('The match could not be started again.'),
                        )
                    }
                  >
                    {again.isPending ? 'Starting…' : 'Run again'}
                  </WorkspaceButton>
                </div>
              </WorkspaceNotice>
            ) : null}

            {actionError ? (
              <WorkspaceNotice tone="critical">
                <p>{actionError}</p>
              </WorkspaceNotice>
            ) : null}

            {restored ? (
              <WorkspaceNotice>
                <div className="font-semibold text-ink-900">
                  {plural(restored.changed.length, 'requirement')} restored to what the spreadsheet
                  says.
                </div>
              </WorkspaceNotice>
            ) : restorePreview.data && restorePreview.data.to_change > 0 ? (
              <RestoreCard
                preview={restorePreview.data}
                busy={busy}
                pending={restoreMutation.isPending}
                onRestore={() => restore(restorePreview.data!)}
              />
            ) : null}

            {lastApply ? (
              <WorkspaceNotice>
                <div className="font-semibold text-ink-900">{applyResultMessage(lastApply)}</div>
                <button
                  type="button"
                  className={cn(linkishClass(), 'mt-1')}
                  onClick={() => onShowLoad(lastApply.load_id)}
                >
                  Show them in the requirements list
                </button>
              </WorkspaceNotice>
            ) : done ? (
              <WorkspaceNotice>
                Every row of this spreadsheet is decided and applied:{' '}
                {plural(run.counts.added, 'row')} added, {run.counts.skipped.toLocaleString()}{' '}
                skipped.
              </WorkspaceNotice>
            ) : null}

            {reviewable ? (
              <MatchReview
                run={run}
                modules={modules}
                busy={busy}
                onDecide={(rowId, input) =>
                  decideOne
                    .mutateAsync({ rowId, input })
                    .then(() => setActionError(null), fail('The decision was not saved.'))
                }
                onBulk={(decisions) =>
                  decideMany
                    .mutateAsync(decisions)
                    .then(() => setActionError(null), fail('The decisions were not saved.'))
                }
              />
            ) : null}
          </>
        )}
      </div>
    </Sheet>
  );
}

/**
 * "Restore the spreadsheet's wording and areas", for a run applied before
 * Caliber kept the client's wording: what would change, in the server's
 * own counts, and the one action.
 */
function RestoreCard({
  preview,
  busy,
  pending,
  onRestore,
}: {
  preview: RestorePreview;
  busy: boolean;
  pending: boolean;
  onRestore: () => void;
}) {
  return (
    <section
      aria-label="Restore the spreadsheet’s wording and areas"
      className="flex flex-col gap-2 rounded-card border border-l-4 border-ink-100 border-l-accent-amber-600 bg-paper-0 p-4"
    >
      <h3 className="text-h3 font-semibold text-ink-900">
        Restore the spreadsheet&rsquo;s wording and areas
      </h3>
      <p className="text-body-sm text-ink-700">
        {plural(preview.to_change, 'requirement')} from {preview.file_name} differ from what the
        client wrote. Caliber now keeps the client&rsquo;s wording and areas; restoring puts these
        back. Numbers and citations stay, and every change is in the requirement&rsquo;s activity.
      </p>
      <ul className="list-disc pl-5 text-body-sm text-ink-700">
        {restoreLines(preview).map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      <div>
        <WorkspaceButton variant="primary" disabled={busy} onClick={onRestore}>
          {pending ? 'Restoring…' : `Restore ${plural(preview.to_change, 'requirement')}`}
        </WorkspaceButton>
      </div>
    </section>
  );
}

/** The engagement's latest matches, newest first, to open again. */
function EarlierRuns({
  runs,
  loading,
  error,
  onOpen,
}: {
  runs: ReadonlyArray<MatchRunSummary>;
  loading: boolean;
  error: unknown;
  onOpen: (id: string) => void;
}) {
  if (error) return <ErrorMessage error={error} />;
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-body-sm text-ink-500">
        <Spinner /> Loading earlier matches…
      </div>
    );
  }
  if (runs.length === 0) return null;
  return (
    <section aria-label="Earlier matches" className="flex flex-col gap-2">
      <h3 className="text-[11px] font-medium uppercase tracking-[0.06em] text-ink-500">
        Earlier matches
      </h3>
      <ul className="divide-y divide-ink-100 rounded-card border border-ink-100 bg-paper-0">
        {runs.map((r) => (
          <li key={r.id} className="flex items-center gap-3 px-3.5 py-2.5">
            <div className="min-w-0 flex-1">
              <div className="truncate font-mono text-meta font-medium text-ink-900">
                {r.file_name}
              </div>
              <div className="mt-0.5 text-meta text-ink-500">
                {formatDate(r.created_at)}
                {r.created_by_name ? ` by ${r.created_by_name}` : ''} ·{' '}
                {plural(r.counts.rows, 'row')}, {r.counts.added.toLocaleString()} added,{' '}
                {r.counts.undecided.toLocaleString()} to decide
              </div>
            </div>
            <StatusChip tone={STATUS_TONE[r.status]}>{STATUS_LABEL[r.status]}</StatusChip>
            <WorkspaceButton
              aria-label={`Open the match of ${r.file_name}`}
              onClick={() => onOpen(r.id)}
            >
              Open
            </WorkspaceButton>
          </li>
        ))}
      </ul>
    </section>
  );
}
